import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Stethoscope,
  LayoutDashboard,
  FileText,
  Globe,
  MessageSquare,
  Settings as SettingsIcon,
  History,
  LogOut,
  Loader2,
  Shield,
  MapPin,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SEO } from "@/components/SEO";

type LoginHistoryEntry = {
  id: number;
  userId: string;
  location: string;
  loggedInAt: string;
};

type LoginHistoryResponse = {
  entries: LoginHistoryEntry[];
};

export default function Security() {
  const { user, logout, isLoading: authLoading } = useAuth();

  const { data, isLoading, isError } = useQuery<LoginHistoryResponse>({
    queryKey: ["/api/login-history"],
  });

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) {
    window.location.href = "/api/login";
    return null;
  }

  const entries = data?.entries ?? [];

  return (
    <div className="h-screen flex flex-col bg-background">
      <SEO title="Security — LRScribe" noindex />
      <header className="h-16 border-b border-border bg-card flex items-center justify-between px-6 shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Stethoscope className="w-5 h-5 text-primary" />
          </div>
          <span className="font-heading font-bold text-lg text-foreground">
            LRScribe
          </span>
        </div>
        <nav className="flex items-center gap-6">
          <Link
            href="/dashboard"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <LayoutDashboard className="w-4 h-4" />
            Dashboard
          </Link>
          <Link
            href="/templates"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <FileText className="w-4 h-4" />
            Templates
          </Link>
          <Link
            href="/rules"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <Globe className="w-4 h-4" />
            Rules
          </Link>
          <Link
            href="/chat"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <MessageSquare className="w-4 h-4" />
            Chat
          </Link>
        </nav>
        <div className="flex items-center gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-3 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                data-testid="button-user-menu"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user.profileImageUrl || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary text-sm">
                    {user.firstName?.charAt(0) || user.email?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
                <span
                  className="text-sm font-medium text-foreground hidden sm:block"
                  data-testid="text-user-name"
                >
                  {user.firstName
                    ? `${user.firstName} ${user.lastName || ""}`.trim()
                    : user.email}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link href="/settings" data-testid="link-settings">
                  <SettingsIcon className="w-4 h-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/security" data-testid="link-security">
                  <Shield className="w-4 h-4" />
                  Security
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/change-log" data-testid="link-change-log">
                  <History className="w-4 h-4" />
                  Change log
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => logout()}
                className="text-destructive focus:text-destructive"
                data-testid="button-logout"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ThemeToggle />
        </div>
      </header>

      <div className="flex-1 overflow-auto">
        <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Security</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Your 10 most recent login locations, most recent first.
            </p>
          </div>

          {isLoading ? (
            <div className="space-y-3" data-testid="security-loading">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-16 bg-muted/50 border border-border rounded-lg animate-pulse"
                />
              ))}
            </div>
          ) : isError ? (
            <div
              className="border border-border rounded-lg p-6 bg-card text-center"
              data-testid="security-error"
            >
              <p className="text-sm text-muted-foreground">
                Couldn't load login history. Please try again.
              </p>
            </div>
          ) : entries.length === 0 ? (
            <div
              className="border border-border rounded-lg p-8 bg-card text-center"
              data-testid="security-empty"
            >
              <Shield className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-medium text-foreground">
                No login history yet
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Login events will appear here after your next sign-in.
              </p>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="security-entries">
              {entries.map((entry) => {
                let relativeLabel = "";
                let absoluteLabel = "";
                try {
                  const d = new Date(entry.loggedInAt);
                  relativeLabel = formatDistanceToNow(d, { addSuffix: true });
                  absoluteLabel = format(d, "MMM d, yyyy 'at' h:mm a");
                } catch {
                  relativeLabel = entry.loggedInAt;
                  absoluteLabel = entry.loggedInAt;
                }
                return (
                  <li
                    key={entry.id}
                    className="border border-border rounded-lg p-4 bg-card flex items-start gap-3"
                    data-testid={`entry-login-${entry.id}`}
                  >
                    <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-sm font-medium text-foreground"
                        data-testid={`text-location-${entry.id}`}
                      >
                        {entry.location}
                      </p>
                      <p
                        className="text-xs text-muted-foreground mt-0.5"
                        title={absoluteLabel}
                        data-testid={`text-time-${entry.id}`}
                      >
                        {relativeLabel}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
